import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameMonth,
    isToday,
    startOfMonth,
    startOfWeek,
    subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import api from '../../api';
import Loading from '../../components/Loading';
import { useFeatureFlag } from '../../helpers/featureFlags';
import { applyName } from '../../helpers/captureMode';
import { visitStatusLabel } from './partnerLabels';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function monthBounds(date) {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return {
        from: format(start, 'yyyy-MM-dd'),
        to: format(end, 'yyyy-MM-dd'),
        label: format(start, "MMMM 'de' yyyy", { locale: ptBR }),
    };
}

function buildMonthGrid(viewMonth) {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
}

function PartnersCalendarPage() {
    const { enabled, loading: flagLoading } = useFeatureFlag('commerce_partners');
    const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState('');
    const [modalOpen, setModalOpen] = useState(false);

    const { from, to, label: monthLabel } = useMemo(() => monthBounds(viewMonth), [viewMonth]);
    const days = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);
    const weekCount = Math.ceil(days.length / 7);

    useEffect(() => {
        if (!enabled) return undefined;
        setLoading(true);
        api.get('/commerce-partners/calendar', { params: { from, to } })
            .then((response) => setVisits(response?.data?.data || []))
            .catch(() => setVisits([]))
            .finally(() => setLoading(false));
        return undefined;
    }, [enabled, from, to]);

    const byDate = useMemo(() => {
        const map = {};
        visits.forEach((visit) => {
            const key = String(visit.planned_on || '').slice(0, 10);
            if (!key) return;
            if (!map[key]) map[key] = [];
            map[key].push(visit);
        });
        return map;
    }, [visits]);

    const openDay = (day) => {
        const key = format(day, 'yyyy-MM-dd');
        if (!(byDate[key]?.length)) return;
        setSelectedDate(key);
        setModalOpen(true);
    };

    const goToday = () => {
        setViewMonth(startOfMonth(new Date()));
        setModalOpen(false);
        setSelectedDate('');
    };

    const shiftMonth = (delta) => {
        setViewMonth((current) => startOfMonth(delta < 0 ? subMonths(current, 1) : addMonths(current, 1)));
        setModalOpen(false);
        setSelectedDate('');
    };

    if (flagLoading) return <Loading />;
    if (!enabled) return <Navigate to="/admin" replace />;

    const dayVisits = selectedDate ? (byDate[selectedDate] || []) : [];
    const selectedLabel = selectedDate
        ? new Date(`${selectedDate}T12:00:00`).toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        })
        : '';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 120px)',
                minHeight: 520,
                gap: 1.5,
            }}
        >
            <Breadcrumbs>
                <Link component={RouterLink} to="/admin/partners" underline="hover" color="inherit">Parcerias</Link>
                <Typography color="text.primary">Calendário</Typography>
            </Breadcrumbs>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                alignItems={{ sm: 'center' }}
            >
                <Typography variant="h5" sx={{ flex: 1 }}>Calendário de visitas</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button
                        size="small"
                        variant="outlined"
                        component={RouterLink}
                        to="/admin/partners"
                    >
                        Lista
                    </Button>
                    <Button size="small" variant="outlined" onClick={goToday}>Hoje</Button>
                    <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="Mês anterior">
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => shiftMonth(1)} aria-label="Próximo mês">
                        <ChevronRightIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{ minWidth: { sm: 220 }, textTransform: 'capitalize', fontWeight: 600 }}
                    >
                        {monthLabel}
                    </Typography>
                </Stack>
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderColor: 'divider',
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                    }}
                >
                    {WEEKDAYS.map((label) => (
                        <Typography
                            key={label}
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                py: 1,
                                textAlign: 'center',
                                fontWeight: 700,
                                letterSpacing: 0.06,
                                textTransform: 'uppercase',
                                borderRight: '1px solid',
                                borderColor: 'divider',
                                '&:last-child': { borderRight: 0 },
                            }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {loading ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Loading />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                            gridTemplateRows: `repeat(${weekCount}, 1fr)`,
                        }}
                    >
                        {days.map((day, index) => {
                            const key = format(day, 'yyyy-MM-dd');
                            const inMonth = isSameMonth(day, viewMonth);
                            const today = isToday(day);
                            const dayList = byDate[key] || [];
                            const hasVisits = dayList.length > 0;
                            const isLastCol = (index + 1) % 7 === 0;
                            const isLastRow = index >= days.length - 7;
                            const visibleVisits = dayList.slice(0, 3);
                            const extra = dayList.length - visibleVisits.length;

                            return (
                                <Box
                                    key={key}
                                    component="button"
                                    type="button"
                                    onClick={() => openDay(day)}
                                    disabled={!hasVisits}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        gap: 0.5,
                                        m: 0,
                                        p: 0.75,
                                        minWidth: 0,
                                        minHeight: 0,
                                        textAlign: 'left',
                                        cursor: hasVisits ? 'pointer' : 'default',
                                        border: 0,
                                        borderRight: isLastCol ? 0 : '1px solid',
                                        borderBottom: isLastRow ? 0 : '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'transparent',
                                        color: 'inherit',
                                        font: 'inherit',
                                        opacity: inMonth ? 1 : 0.45,
                                        transition: 'background-color 0.15s ease',
                                        '&:hover': hasVisits ? {
                                            bgcolor: (theme) => theme.palette.mode === 'dark'
                                                ? 'rgba(25, 118, 210, 0.12)'
                                                : 'rgba(25, 118, 210, 0.06)',
                                        } : undefined,
                                        '&:disabled': {
                                            color: 'inherit',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.25 }}>
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.85rem',
                                                fontWeight: today || hasVisits ? 700 : 500,
                                                bgcolor: today ? 'primary.main' : 'transparent',
                                                color: today ? 'primary.contrastText' : 'text.primary',
                                            }}
                                        >
                                            {format(day, 'd')}
                                        </Box>
                                    </Box>

                                    <Stack spacing={0.35} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                        {visibleVisits.map((visit) => (
                                            <Box
                                                key={visit.uuid}
                                                sx={{
                                                    px: 0.6,
                                                    py: 0.2,
                                                    borderRadius: 0.75,
                                                    bgcolor: visit.status === 'done'
                                                        ? (theme) => theme.palette.mode === 'dark'
                                                            ? 'rgba(76, 175, 80, 0.22)'
                                                            : 'rgba(76, 175, 80, 0.16)'
                                                        : (theme) => theme.palette.mode === 'dark'
                                                            ? 'rgba(25, 118, 210, 0.28)'
                                                            : 'rgba(25, 118, 210, 0.14)',
                                                    borderLeft: '3px solid',
                                                    borderColor: visit.status === 'done' ? 'success.main' : 'primary.main',
                                                }}
                                            >
                                                <Typography
                                                    noWrap
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1.3,
                                                    }}
                                                >
                                                    {applyName(visit.partner_name)}
                                                </Typography>
                                            </Box>
                                        ))}
                                        {extra > 0 ? (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ px: 0.4, fontWeight: 600 }}
                                            >
                                                +{extra} mais
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ textTransform: 'capitalize', pb: 1 }}>
                    Visitas em {selectedLabel}
                </DialogTitle>
                <DialogContent dividers>
                    {dayVisits.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Nenhuma visita neste dia.</Typography>
                    ) : (
                        <Stack spacing={0}>
                            {dayVisits.map((visit) => (
                                <Stack
                                    key={visit.uuid}
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1}
                                    alignItems={{ sm: 'center' }}
                                    sx={{
                                        py: 1.25,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        '&:last-child': { borderBottom: 0 },
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography fontWeight={600}>{applyName(visit.partner_name)}</Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>
                                            {[visit.partner_address, visit.partner_city].filter(Boolean).join(' · ') || 'Sem endereço'}
                                        </Typography>
                                        <Chip size="small" label={visitStatusLabel(visit.status)} sx={{ mt: 0.5 }} />
                                    </Box>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        <Button size="small" component={RouterLink} to={`/admin/partners/${visit.partner_uuid}`}>
                                            Detalhes
                                        </Button>
                                        {visit.status === 'done' ? (
                                            <>
                                                <Button size="small" component={RouterLink} to={`/admin/partners/visits/${visit.uuid}/receipt`}>
                                                    Comprovante
                                                </Button>
                                                <Button size="small" component={RouterLink} to={`/admin/partners/visits/${visit.uuid}`}>
                                                    Ver registro
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                component={RouterLink}
                                                to={`/admin/partners/visits/${visit.uuid}`}
                                            >
                                                + Visita
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedDate ? (
                        <Button component={RouterLink} to={`/admin/partners/map?date=${selectedDate}`} sx={{ mr: 'auto' }}>
                            Mapa do dia
                        </Button>
                    ) : null}
                    <Button onClick={() => setModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default PartnersCalendarPage;
